/**
 * BackgroundJobTable Component
 *
 * Displays a table of background jobs with status indicators and actions.
 */

import React from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../../components/ui/Table';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { BackgroundJob, JobStatus } from '../../../types/background-job.types';

interface BackgroundJobTableProps {
  jobs: BackgroundJob[];
  isLoading: boolean;
  onViewDetails: (job: BackgroundJob) => void;
}

/**
 * Get badge variant based on job status
 */
function getStatusBadgeVariant(status: JobStatus): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
      return 'warning';
    case 'failed':
      return 'error';
    case 'pending':
    default:
      return 'default';
  }
}

/**
 * Get human-readable status label
 */
function getStatusLabel(status: JobStatus): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'running':
      return 'Running';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
}

/**
 * Format duration between two timestamps
 */
function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '-';

  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationMs = end - start;

  if (durationMs < 1000) return `${durationMs}ms`;
  if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
  if (durationMs < 3600000) return `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s`;
  return `${Math.floor(durationMs / 3600000)}h ${Math.floor((durationMs % 3600000) / 60000)}m`;
}

/**
 * Format timestamp to human-readable
 */
function formatTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString();
}

/**
 * Get initiator display name
 */
function getInitiatorDisplay(job: BackgroundJob): { name: string; type: string } {
  const { authContext } = job;
  if (authContext.type === 'schedule') {
    return {
      name: `Schedule: ${authContext.scheduleName || authContext.scheduleId}`,
      type: 'schedule',
    };
  }
  if (authContext.type === 'application_grant') {
    return {
      name: `App: ${authContext.sourceApplicationCode || authContext.sourceApplicationId}`,
      type: 'application_grant',
    };
  }
  return {
    name: authContext.initiatorName || authContext.initiatorId,
    type: 'Direct call',
  };
}

export const BackgroundJobTable: React.FC<BackgroundJobTableProps> = ({
  jobs,
  isLoading,
  onViewDetails,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={7} />;
  }

  if (jobs.length === 0) {
    return (
      <EmptyState
        title="No background jobs found"
        description="No background jobs found."
      />
    );
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>Job ID</TableHeader>
          <TableHeader>Function</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Initiator</TableHeader>
          <TableHeader>Duration</TableHeader>
          <TableHeader>Created</TableHeader>
          <TableHeader>Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {jobs.map((job) => {
            const initiator = getInitiatorDisplay(job);
            return (
              <TableRow key={job.jobId}>
                <TableCell>
                  <span className="font-mono text-xs text-gray-600" title={job.jobId}>
                    {job.jobId.slice(0, 8)}...
                  </span>
                </TableCell>
                <TableCell>
                  <span className="font-medium text-gray-900">{job.functionName}</span>
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(job.status)}>
                    {getStatusLabel(job.status)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="text-gray-900">{initiator.name}</div>
                    <div className="text-xs text-gray-500">{initiator.type}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-600">
                    {formatDuration(job.startedAt, job.completedAt)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-600">{formatTimestamp(job.createdAt)}</span>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDetails(job)}
                    title="View details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
};

export default BackgroundJobTable;
